// @flow

import * as React from 'react';
import { localize } from '@ta/common.i18n';
import classnames from '@ta/common.classnames';
import { featureIsEnabled } from '@ta/common.features';
import { Icon } from '@ta/styleguide.icon';
import { ClickTracker } from '@ta/common.tracking';
import { InteractionLogger } from '@ta/tracking.interactions';
import { TopLevelNavLink } from '@ta/brand.global-nav';
import type { NavLinkType } from '@ta/brand.global-nav';
import { GeoPillFlyout } from '@ta/brand.trip-search-geopill-flyout';
import type { GeoPillSponsorDataProvider } from '@ta/brand.sponsored-geopill';

import QuickLinksMoreMenu from './QuickLinksMoreMenu';
import type { QuickLinksMoreMenuData } from './types';
import styles from './QuickLinksLiteItem.less';

// These classes are dynamically referenced:
/* eslint css-modules/no-unused-class: [2, { markAsUsed:
  ['order-hotels', 'order-vacation-rentals', 'order-travelers-choice-badge', 'order-restaurants', 'order-attractions', 'order-flights',
  'order-airlines', 'order-forums', 'order-shopping-bag-fill', 'order-cruises', 'order-parking', 'order-on-the-beach', 'order-guides-fill']
}] */
import commonStyles from './commonStyles.less';

type Props = {|
  /** Whether this tile shows as the active/current link */
  isActive?: boolean,

  /** Data model for link. Not needed if this is the "more" tile */
  link?: NavLinkType,

  /** If this is the more tile we show a popover */
  isMoreTrigger?: boolean,

  /** If isMoreTile=true and we want the more popover to show as fixed */
  morePopoverFixed?: boolean,

  /** Data model for the quick links more menu, if this is the "more" tile. */
  collapsedData?: QuickLinksMoreMenuData,

  /** Whether the page we are on is scoped to a geo. If not, we pop the geopill flyout. */
  isGeoScoped?: boolean,

  /** Whether to show just text or just an icon. */
  displayMode: 'TEXT' | 'ICON',

  /** Which color scheme to use */
  color: 'green' | 'black',

  /** Determine whether we shows a link before the "more" dropdown. */
  extendedQuickLinks?: boolean,

  /** Promise for future geopill sponsor result or null if not enabled */
  sponsorDataProvider?: ?GeoPillSponsorDataProvider,

  /** Invoked when the geo pill overlay is opened */
  onGeoPillOpen?: () => void,

  /** Invoked when the geo pill overlay is closed */
  onGeoPillClose?: () => void,
|};

const QuickLinksLiteItem = (props: Props) => {
  let wrapClasses = classnames('ui_column', styles.item, {
    [styles.extendedQuickLinks]: props.extendedQuickLinks,
    [styles.green]: props.color === 'green',
    [styles.black]: props.color === 'black',
  });
  const { link } = props;
  const isIconDisplayMode = props.displayMode === 'ICON';

  if (
    link &&
    link.icon &&
    commonStyles[`order-${link.icon}`] &&
    !featureIsEnabled('quicklinks_sorting_model')
  ) {
    wrapClasses = classnames(wrapClasses, commonStyles[`order-${link.icon}`], {
      [styles.active]: props.isActive,
      [styles.iconOnly]: isIconDisplayMode,
    });
  } else if (props.isMoreTrigger) {
    wrapClasses = classnames(wrapClasses, commonStyles['order-more'], {
      [styles.iconOnly]: isIconDisplayMode,
    });
  }

  let contents;
  if (props.isMoreTrigger && props.collapsedData) {
    contents = (
      <QuickLinksMoreMenu collapsedData={props.collapsedData} fixedPopover={props.morePopoverFixed}>
        <div className={styles.link}>
          <ClickTracker module="QuickLinksMore" action="click" context="open">
            {isIconDisplayMode ? (
              <Icon name="more-horizontal" />
            ) : (
              localize('common_more_capital_ffffe27f')
            )}
            {/* More */}
          </ClickTracker>
        </div>
      </QuickLinksMoreMenu>
    );
  } else if (link) {
    const willOpenGeopill =
      (!!link.urlWithGeoTemplate || !!link.urlWithLatLngTemplate) && !props.isGeoScoped;
    const newLabelElement = link.promoteAsNew && (
      <span className={styles.newLabel}>{localize('common_NEW_1bd8')}</span> // NEW
    );
    const navLink = setTarget => (
      <InteractionLogger>
        {logInteraction => (
          <TopLevelNavLink
            setTarget={setTarget}
            preventDefault={willOpenGeopill}
            link={isIconDisplayMode ? { ...link, text: '' } : link}
            showQuickLinkIcon={isIconDisplayMode}
            extraClasses={classnames(styles.link, { [styles.withNewLabel]: link.promoteAsNew })}
            trackingModule="QuickLinks"
            extraLabel={newLabelElement}
            nonLink={link.id === 'tourism' || link.id === 'scoped-home'}
            onClick={e => logInteraction(e, 'quickLinkClickV2', { trackingKey: link.id })}
          />
        )}
      </InteractionLogger>
    );

    // Only pop geopill flyout if we're not scoped and the link (e.g., hotels) supports the geo param.
    if (willOpenGeopill) {
      contents = (
        <GeoPillFlyout
          latLngUrlTemplate={link.urlWithLatLngTemplate}
          geoUrlTemplate={link.urlWithGeoTemplate}
          sponsorDataProvider={props.sponsorDataProvider}
          uiOrigin={`QuickLink_${link.trackingLabel}_geopicker`}
          onOpen={props.onGeoPillOpen}
          onClose={props.onGeoPillClose}
        >
          {setTarget => navLink(setTarget)}
        </GeoPillFlyout>
      );
    } else {
      contents = navLink();
    }
  } else {
    contents = null;
  }

  return <span className={wrapClasses}>{contents}</span>;
};

QuickLinksLiteItem.defaultProps = {
  isActive: false,
  link: undefined,
  isMoreTrigger: false,
  morePopoverFixed: false,
  collapsedData: undefined,
  isGeoScoped: false,
  extendedQuickLinks: false,
};

export default QuickLinksLiteItem;
